import {
  Box,
  TextField,
  debounce,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Button,
  Slider,
} from "@mui/material";
import { WebsiteFilterPropertiesDto } from "@serviestate/shared";
import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { useAgency } from "../../hooks/use-agency";
import { getTranslationFromArray } from "../../utils";

export type FilterFormProps = {
  filters: WebsiteFilterPropertiesDto;
  onSearch: (form: WebsiteFilterPropertiesDto) => Promise<void>;
};

export default function FilterForm({ filters, onSearch }: FilterFormProps) {
  const { t, i18n } = useTranslation();
  const inputReferenceRef = useRef<HTMLInputElement>(null);

  const agency = useAgency();

  const propertyType = agency.info.property_types.find(
    (x) => x.id === filters.property_type_id
  );

  const transactionType = agency.info.transactions.find(
    (x) => x.id === filters.transaction_type_id
  );

  const [tempPriceRange, setTempPriceRange] = useState<[number, number]>([
    0,
    transactionType?.max_price ?? 0,
  ]);

  const handleSearch = async (
    overrides?: Partial<WebsiteFilterPropertiesDto>
  ) => {
    await onSearch({
      ...filters,
      ...overrides,
    });
  };

  const handleClear = useCallback(() => {
    if (inputReferenceRef.current) inputReferenceRef.current.value = "";
    onSearch({
      ...filters,
      min_price: null,
      max_price: null,
      min_bedrooms: null,
      min_bathrooms: null,
      min_surface: null,
      reference: null,
      geo_zone_id: null,
      property_subtype_id: null,
      extras: null,
      conservation: null,
      available: null,
    });
  }, [onSearch, filters]);

  useEffect(() => {
    if (inputReferenceRef.current)
      inputReferenceRef.current.value = filters.reference ?? "";
    setTempPriceRange([
      filters.min_price ?? 0,
      filters.max_price ?? transactionType?.max_price ?? 0,
    ]);
  }, [
    filters.reference,
    filters.min_price,
    filters.max_price,
    transactionType,
  ]);

  const typeSelectedName = propertyType
    ? getTranslationFromArray(i18n.language, propertyType.translations)?.name ??
      propertyType.name
    : null;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <TextField
        inputRef={inputReferenceRef}
        fullWidth
        label={t("filter.reference")}
        onChange={debounce(
          (e) => handleSearch({ reference: e.target.value }),
          500
        )}
      />
      <FormControl fullWidth>
        <InputLabel>{t("transactional.zones")}</InputLabel>
        <Select
          label={t("transactional.zones")}
          value={filters.geo_zone_id ?? "all"}
          onChange={(e) => {
            const value = e.target.value === "all" ? null : e.target.value;
            handleSearch({ geo_zone_id: value });
          }}
        >
          <MenuItem value="all">{t("geo.all_locations")}</MenuItem>
          {agency.info.geo_zones.map((x) => (
            <MenuItem key={`zone_${x.id}`} value={x.id}>
              {x.computed_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl defaultValue={1}>
        <InputLabel>{t("transactional.property_type")}</InputLabel>
        <Select
          label={t("transactional.property_type")}
          value={filters.property_type_id ?? "all"}
          onChange={(e) => {
            const value = e.target.value === "all" ? null : e.target.value;
            if (value !== null && typeof value !== "number") return;
            handleSearch({ property_type_id: value });
          }}
        >
          <MenuItem value="all">{t("anything")}</MenuItem>
          {agency.info.property_types.map((x) => (
            <MenuItem key={`type_${x.id}`} value={x.id}>
              {getTranslationFromArray(i18n.language, x.translations)?.name ??
                x.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {propertyType && (
        <FormControl defaultValue={1}>
          <InputLabel>
            {t(`transactional.type_of`, {
              type: typeSelectedName?.toLocaleLowerCase(),
            })}
          </InputLabel>
          <Select
            label={t(`transactional.type_of`, {
              type: typeSelectedName?.toLocaleLowerCase(),
            })}
            value={filters.property_subtype_id ?? "all"}
            onChange={(e) => {
              const value =
                e.target.value !== "all" ? (e.target.value as number) : null;
              handleSearch({ property_subtype_id: value });
            }}
          >
            <MenuItem value="all">{t("anything")}</MenuItem>
            {propertyType &&
              agency.info.property_subtypes
                .filter((x) => x.type_id === propertyType.id)
                .map((x) => (
                  <MenuItem key={`subtype_${x.id}`} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
      )}
      <FormControl>
        <Typography>{t("transactional.price")}</Typography>
        <Typography variant="body2">
          {`${tempPriceRange[0].toLocaleString(
            "de-DE"
          )}€ - ${tempPriceRange[1].toLocaleString("de-DE")}€`}
        </Typography>
        <Box sx={{ mx: 1.5 }}>
          <Slider
            value={tempPriceRange}
            step={filters.transaction_type_id === 1 ? 50 : 10000}
            min={0}
            max={transactionType?.max_price ?? 0}
            onChange={(_, value) =>
              setTempPriceRange(value as [number, number])
            }
            onChangeCommitted={(_, value) => {
              handleSearch({
                min_price: (value as number[])[0],
                max_price: (value as number[])[1],
              });
            }}
          />
        </Box>
      </FormControl>
      <FormControl defaultValue={1}>
        <InputLabel>{t("transactional.rooms")}</InputLabel>
        <Select
          label={t("transactional.rooms")}
          value={filters.min_bedrooms ?? "any"}
          onChange={(e) => {
            const value =
              e.target.value !== "any" ? (e.target.value as number) : null;
            handleSearch({
              min_bedrooms: value,
            });
          }}
        >
          <MenuItem value="any">{t("transactional.without_limit")}</MenuItem>
          <MenuItem value={1}>1 {t("transactional.or_more_rooms")}</MenuItem>
          <MenuItem value={2}>2 {t("transactional.or_more_rooms")}</MenuItem>
          <MenuItem value={3}>3 {t("transactional.or_more_rooms")}</MenuItem>
          <MenuItem value={4}>4 {t("transactional.or_more_rooms")}</MenuItem>
        </Select>
      </FormControl>
      <FormControl defaultValue={1}>
        <InputLabel>{t("transactional.baths")}</InputLabel>
        <Select
          label={t("transactional.baths")}
          value={filters.min_bathrooms ?? "any"}
          onChange={(e) => {
            const value =
              e.target.value === "any" ? null : (e.target.value as number);
            handleSearch({ min_bathrooms: value });
          }}
        >
          <MenuItem value="any">{t("transactional.without_limit")}</MenuItem>
          <MenuItem value={"+1"}>1 {t("transactional.or_more_baths")}</MenuItem>
          <MenuItem value={"+2"}>2 {t("transactional.or_more_baths")}</MenuItem>
        </Select>
      </FormControl>
      <FormControl defaultValue={1}>
        <InputLabel>{t("transactional.surface")}</InputLabel>
        <Select
          label={t("transactional.surface")}
          value={filters.min_surface ?? "any"}
          onChange={(e) => {
            const value =
              e.target.value === "any" ? null : (e.target.value as number);
            handleSearch({ min_surface: value });
          }}
        >
          <MenuItem value="any">{t("transactional.without_limit")}</MenuItem>
          {Array.from({ length: 20 }).map((_, i) => {
            const x = i + 1;
            const value =
              x > 16
                ? 10 * 20 + 6 * 50 + (x - 16) * 100
                : x > 10
                ? 10 * 20 + (x - 10) * 50
                : x * 20;
            return (
              <MenuItem key={`surface_${i}`} value={value}>
                {t("transactional.from")} {value} m<sup>2</sup>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Box>
        <Typography gutterBottom>{t("transactional.extras")}</Typography>
        <Grid container spacing={1}>
          {agency.info.attribute_extras.map((attr) => {
            const translation = getTranslationFromArray(
              i18n.language,
              attr.translations
            );
            return (
              <Grid key={`attr_${attr.code}`} item>
                <Chip
                  label={translation?.name ?? attr.name}
                  onClick={() => {
                    handleSearch({
                      extras: filters.extras?.includes(attr.code)
                        ? filters.extras.filter((x) => x !== attr.code)
                        : [...(filters.extras ?? []), attr.code],
                    });
                  }}
                  color={
                    filters.extras?.includes(attr.code) ? "primary" : "default"
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box>
        <Typography gutterBottom>{t("transactional.conservation")}</Typography>
        <Grid container spacing={1}>
          {agency.info.conservations.map((attr) => {
            const translation = getTranslationFromArray(
              i18n.language,
              attr.translations
            );
            return (
              <Grid key={`attr_${attr.attribute_code}`} item>
                <Chip
                  label={translation?.label ?? attr.label}
                  onClick={() => {
                    handleSearch({
                      conservation: filters.conservation?.includes(attr.value)
                        ? filters.conservation.filter((x) => x !== attr.value)
                        : [...(filters.conservation ?? []), attr.value],
                    });
                  }}
                  color={
                    filters.conservation?.includes(attr.value)
                      ? "primary"
                      : "default"
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box textAlign="center">
        <Button
          variant="contained"
          color="error"
          sx={{ px: 5 }}
          onClick={handleClear}
        >
          {t("transactional.clean_filters")}
        </Button>
      </Box>
    </Box>
  );
}
